import { Grid, Stack } from "@mui/material";
import { BucketsROICharts } from "features/analytics";
import StandardAnalyticsChartStatCard from "features/analytics/components/StandardAnalyticsChartStatCard";
import {
  ApiChartLayout,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiProjectSettingKey,
  ApiYAxisChartType,
} from "@incendium/api";
import { useSelectedProject } from "Hooks";
import { AnalyticsSpacing } from "consts";
import EcomStatsAndFunnel from "features/analytics/components/EcomStatsAndFunnel";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_ECOM_INSIGHTS_CHANNEL_REVENUE,
  CHART_KEY_ECOM_INSIGHTS_HISTORICAL_CONTRIBUTION_MARGIN,
  CHART_KEY_ECOM_INSIGHTS_LTV_VS_CAC,
  CHART_KEY_ECOM_INSIGHTS_MARKETING_EFFICIENCY,
  CHART_KEY_ECOM_INSIGHTS_OVERALL_CONTRIBUTION_MARGIN,
  CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_LTV_CAC_RATIO,
  CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_NET_REVENUE,
  CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_NEW_CUSTOMER_CAC,
  CHART_KEY_ECOM_INSIGHTS_TOTAL_NEW_CUSTOMERS_BY_CHANNEL,
  CHART_KEY_ECOM_INSIGHTS_TOTAL_REPEAT_CUSTOMERS_BY_CHANNEL,
  CHART_KEY_ECOM_INSIGHTS_TOTAL_REVENUE,
  CHART_KEY_ECOM_INSIGHTS_TOTAL_SALES_BY_CHANNEL,
} from "features/analytics/constants/chartKeys";

function EcomInsights() {
  const { projectSettings } = useSelectedProject();
  return (
    <Grid container spacing={AnalyticsSpacing} mb={4}>
      <Grid item xs={12}>
        <EcomStatsAndFunnel />
      </Grid>
      <Grid item xs={12} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_TOTAL_REVENUE}
          chart={{
            name: "Total Revenue",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            displayOptions: {
              noLegend: true,
            },
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_REVENUE],
                chart: {
                  [ApiMetric.METRIC_REVENUE]: ApiYAxisChartType.BAR,
                },
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={8} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_OVERALL_CONTRIBUTION_MARGIN}
          chart={{
            name: "Contribution Margin Trend",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSION_CONTRIBUTION_MARGIN],
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_HISTORICAL_CONTRIBUTION_MARGIN}
          overrideDate={{
            lastNDays: 365,
            from: undefined,
            to: undefined,
          }}
          chart={{
            name: "Historical Contribution Margin",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_MONTH],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            includePublisherReadyOnly: true,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSION_CONTRIBUTION_MARGIN],
                chart: {
                  [ApiMetric.METRIC_SESSION_CONTRIBUTION_MARGIN]:
                    ApiYAxisChartType.BAR,
                },
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_MARKETING_EFFICIENCY}
          chart={{
            name: "Marketing Effiency Ratio (MER)",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            includePublisherReadyOnly: true,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSION_EFFICIENCY_RATIO],
              },
            ],
          }}
        />
      </Grid>

      <Grid item xs={8} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_CHANNEL_REVENUE}
          chart={{
            name: "Overall Revenue by Top 5 Channels",
            dimension: [ApiDimension.DIMENSION_CHANNEL],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes: [],
            limit: 5,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_REVENUE],
                chart: {
                  [ApiMetric.METRIC_REVENUE]: ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              layout: ApiChartLayout.VERTICAL,
              noLegend: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <Stack spacing={AnalyticsSpacing}>
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_LTV_CAC_RATIO}
            chart={{
              name: "LTV:CAC Ratio",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              includePublisherReadyOnly: true,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_LTV_CAC_RATIO],
                },
              ],
              displayOptions: {
                statTitle: "LTV:CAC Ratio",
              },
              comparison: true,
            }}
          />
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_NET_REVENUE}
            chart={{
              name: "Net Revenue",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSION_NET_REVENUE],
                },
              ],
              displayOptions: {
                statTitle: "Net Revenue",
              },
              comparison: true,
            }}
          />
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_NEW_CUSTOMER_CAC}
            chart={{
              name: "New Customer CAC",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              includePublisherReadyOnly: true,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_CAC],
                },
              ],
              displayOptions: {
                statTitle: "New Customer CAC",
              },
              comparison: true,
            }}
          />
        </Stack>
      </Grid>
      <Grid item xs={8}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_LTV_VS_CAC}
          chart={{
            name: "LTV:CAC Ratio over time",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            includePublisherReadyOnly: true,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_LTV_CAC_RATIO],
              },
            ],
          }}
        />
      </Grid>

      {projectSettings[
        ApiProjectSettingKey.SHOW_AWARENESS_ACQUISTION_RETENTION_INSIGHTS
      ] === "true" && (
        <Grid item xs={12}>
          <BucketsROICharts
            dimensions={[
              ApiDimension.DIMENSION_CHANNEL,
              ApiDimension.DIMENSION_SOURCE,
              ApiDimension.DIMENSION_CAMPAIGN,
              ApiDimension.DIMENSION_TERM,
            ]}
          />
        </Grid>
      )}
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_TOTAL_SALES_BY_CHANNEL}
          chart={{
            name: "Total Sales by Channel",
            dimension: [ApiDimension.DIMENSION_CHANNEL],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            attributes: [],
            includeEmpty: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SALES_COUNT],
              },
            ],
            displayOptions: {
              showTotals: true,
              noAttributionToggle: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_TOTAL_NEW_CUSTOMERS_BY_CHANNEL}
          chart={{
            name: "Total New Customers by Channel",
            dimension: [ApiDimension.DIMENSION_CHANNEL],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            attributes: [],
            includeEmpty: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_NEW_CUSTOMERS_COUNT],
              },
            ],
            displayOptions: {
              showTotals: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_TOTAL_REPEAT_CUSTOMERS_BY_CHANNEL}
          chart={{
            name: "Total Returning Customers by Channel",
            dimension: [ApiDimension.DIMENSION_CHANNEL],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            attributes: [],
            includeEmpty: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_RETURNING_CUSTOMERS_COUNT],
              },
            ],
            displayOptions: {
              showTotals: true,
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default EcomInsights;
